import React, { useState, useMemo } from "react";
import styles from "../StartingCustomPlan/StartingCustomPlan.module.css";
import StartNowBtn from "../../StartNowBtn/StartNowBtn";
import DeskCustomPlan from "./DeskCustomPlan";
import  createCheckoutUrl  from "../../../lib/Store";
const StartingCustomPlan = ({ country = "USD" }) => {
  const [isYearly, setIsYearly] = useState(true);
  const [calls, setCalls] = useState(10);
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };
  // make sure country code is upper-case and trimmed
  const countryCode = (country || "USD").toString().trim().toUpperCase();
  console.log("Detected country:", countryCode);

  const min = 10;
  const max = 4000;

  // Pricing tiers per currency
  const pricingMap = {
    USD: [
      { min: 1, max: 100, unit: 0.5, flat: 5.0 },
      { min: 101, max: 200, unit: 0.45 },
      { min: 201, max: 300, unit: 0.42 },
      { min: 301, max: 1500, unit: 0.4 },
      { min: 1501, max: 2000, unit: 0.38 },
      { min: 2001, max: 2500, unit: 0.36 },
      { min: 2501, max: 3000, unit: 0.35 },
      { min: 3001, max: 3500, unit: 0.34 },
      { min: 3501, max: 999999, unit: 0.33 },
    ],
    CAD: [
      { min: 1, max: 100, unit: 0.689, flat: 7.0 },
      { min: 101, max: 200, unit: 0.6201 },
      { min: 201, max: 300, unit: 0.5788 },
      { min: 301, max: 1500, unit: 0.5512 },
      { min: 1501, max: 2000, unit: 0.5237 },
      { min: 2001, max: 2500, unit: 0.4961 },
      { min: 2501, max: 3000, unit: 0.4823 },
      { min: 3001, max: 3500, unit: 0.4685 },
      { min: 3501, max: 999999, unit: 0.4547 },
    ],
    AUD: [
      { min: 1, max: 100, unit: 0.7582, flat: 7.5 },
      { min: 101, max: 200, unit: 0.6824 },
      { min: 201, max: 300, unit: 0.6369 },
      { min: 301, max: 1500, unit: 0.6066 },
      { min: 1501, max: 2000, unit: 0.5763 },
      { min: 2001, max: 2500, unit: 0.5459 },
      { min: 2501, max: 3000, unit: 0.5308 },
      { min: 3001, max: 3500, unit: 0.5156 },
      { min: 3501, max: 999999, unit: 0.5004 },
    ],
    GBP: [
      { min: 1, max: 100, unit: 0.3711, flat: 3.7 },
      { min: 101, max: 200, unit: 0.334 },
      { min: 201, max: 300, unit: 0.3117 },
      { min: 301, max: 1500, unit: 0.2969 },
      { min: 1501, max: 2000, unit: 0.282 },
      { min: 2001, max: 2500, unit: 0.2672 },
      { min: 2501, max: 3000, unit: 0.2598 },
      { min: 3001, max: 3500, unit: 0.2524 },
      { min: 3501, max: 999999, unit: 0.2449 },
    ],
  };

  const pricingTiers = pricingMap[countryCode] || pricingMap["USD"];

  const currencySymbol =
    {
      USD: "$",
      CAD: "$",
      AUD: "$",
      GBP: "£",
    }[countryCode] || "$";

  // Compute price using tiers
  const { unitPrice, totalPrice } = useMemo(() => {
    const tier = pricingTiers.find((t) => calls >= t.min && calls <= t.max);
    const unit = tier?.unit ?? 0;
    const flat = tier?.flat ?? 0;
    const total = calls * unit + flat;

    return {
      unitPrice: unit,
      totalPrice: isNaN(total) ? 0 : total, // <-- FIXED HERE
    };
  }, [calls, pricingTiers]);

  // slider helpers
  const [integerPart, decimalPart] = totalPrice?.toFixed(2).split(".");
  const getTooltipPosition = () => ((calls - min) / (max - min)) * 100;
  const percentage = ((calls - min) / (max - min)) * 100;
  const thumbSize = 35;

  // format calls like 1k, 3.5k
  const formatCalls = (num) => {
    const n = Number(num) || 0;
    return n >= 1000
      ? (n / 1000).toFixed(1).replace(".0", "") + "k"
      : String(n);
  };

  // handle Start Now (opens stripe link in new tab)
  const handleStartNow = async () => {
    try {
      const response = await fetch(
        "https://srvr.rxpt.ai/api/tier/checkout/website",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            presetUnits: calls,
            minUnits: min,
            maxUnits: max,
            // if you need to pass currency to backend, include it here
            currency: countryCode,
          }),
        }
      );

      const data = await response.json();
      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        alert("Something went wrong while creating the Stripe session.");
      }
    } catch (err) {
      console.error(err);
      alert("Error creating subscription: " + (err?.message || err));
    }
  };

  // helper to render yearly calls (calls/2.5) with safe formatting and k shorthand
  const renderYearlyCalls = () => {
    const raw = Number(calls) / 2.5;
    if (!isFinite(raw) || isNaN(raw)) return "0 call";

    if (raw >= 1000) {
      const kVal = raw / 1000;
      // show one decimal only when needed
      const formatted = Number.isInteger(kVal)
        ? String(kVal)
        : kVal.toFixed(1).replace(".0", "");
      return `${formatted}k call`;
    }

    return `${Math.round(raw)} call`;
  };

const premiumPlans = {
  starter: {
    name: "Starter",
    min: 200,
    max: 349,
    price: 119,
    callsHandled: 200,
    features: ["Chat & Call Widget", "Built-In Mobile CRM"],
    saving: "10% ($119)",
    img : '/Svg/Starter-Icon.svg'
  },
  scaler: {
    name: "Scaler",
    min: 350,
    max: 759,
    price: 249,
    callsHandled: 400,
    features: ["AI Call Handling", "Free VoIP Number", "Call Logs"],
    saving: "15% ($360)",
    img: '/Svg/scaler-logo.svg'
  },
  growth: {
    name: "Growth",
    min: 760,
    max: 999999,
    price: 599,
    callsHandled: 600,
    features: ["Unlimited Minutes", "Priority Support", "Advanced AI"],
    saving: "20% ($240)",
    img : "/Svg/growth icon.svg"
  },
};
    async function handleGetStarted(e, plan) {
      e?.stopPropagation?.();
     
     
      try {
      // setLoading(true)
        const url = await createCheckoutUrl(plan , "month");
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        console.error("Checkout error:", err);
        alert("Couldn't start checkout. Please try again.");
      } finally {
      //  setLoading(false)
      }
    }
const activePremium =
  calls >= premiumPlans.starter.min && calls <= premiumPlans.starter.max
    ? premiumPlans.starter
    : calls >= premiumPlans.scaler.min && calls <= premiumPlans.scaler.max
    ? premiumPlans.scaler
    : premiumPlans.growth;
  return (
    <>
      <div className={styles.DeskTopView}>
        <div><DeskCustomPlan
  countryCode={countryCode}
  handleStartNow={handleStartNow}
  pricingMap={pricingMap}
/></div>
      </div>









      <div className={styles.mobView}>
        {/* Show new banner when totalPrice >= 109 */}
        <div
  className={`${styles.premiumNoticeWrapper} ${
    totalPrice >= 109 ? styles.show : styles.hide
  }`}
>
  <div className={styles.premiumNoticeCard}>
    <div className={styles.ribbon}>
      <img src="/Svg/better-option-ribion.svg" alt="better-option-ribion" />
    </div>

    <div className={styles.cardContent}>
      <div className={styles.left}>
        <div className={styles.icon}>
          <img src={activePremium?.img} alt="growth icon" />
        </div>

        <div>
          
          <h4 className={styles.title}>{activePremium.name}</h4>
          <p className={styles.subtitle}>
            {activePremium.callsHandled} calls handled
          </p>
        </div>
      </div>

      <div className={styles.right}>
        <p className={styles.price2}>${activePremium.price}</p>
        <p className={styles.period}>Monthly</p>
      </div>

      <div className={styles.arrowWrapper} onClick={toggleDropdown}>
        <img
          src="/Svg/drop-Arrow.svg"
          alt="drop-Arrow"
          className={`${styles.arrowIcon} ${isOpen ? styles.rotate : ""}`}
        />
      </div>
    </div>

    <div
      className={`${styles.dropdownContent} ${
        isOpen ? styles.showDropdown : ""
      }`}
    >
      <div className={styles.featureSection}>
        <div className={styles.totalMin}>
          <h3 className={styles.minutes}>{calls}</h3>
          <p className={styles.mins}>minutes</p>
        </div>

        <div className={styles.features2}>
          {activePremium.features.map((f, i) => (
            <p key={i} className={styles.featureTag}>
              {f}
            </p>
          ))}
        </div>
      </div>

      <div className={styles.savingBanner}>
        You saved <strong>{activePremium.saving}</strong> on Yearly billing
      </div>

      <p className={styles.standardFeature}>+ Standard Feature</p>

      <button className={styles.getStartedBtn} onClick={(e)=>handleGetStarted(e , activePremium.name)}>Get Started</button>
    </div>
  </div>
</div>


        <div className={styles.containerBox}>

          <div className={styles.container}>
            <div className={styles.card}>

              {/* Toggle */}
              <div className={styles.topbar}>
                <div><h2>My Custom Plan</h2></div>
                <div className={styles.toggleRow}>
                  <div
                    className={`${styles.toggleSwitch} ${isYearly ? styles.yearly : ""
                      }`}
                    onClick={() => setIsYearly(!isYearly)}
                  >
                    <div className={styles.circle}></div>
                  </div>
                  <p className={isYearly ? styles.activeLabel : ""}>
                    Calculate Calls
                  </p>
                </div>
              </div>


              {/* Pricing Section */}
              <div className={styles.priceSection}>
                <div className={styles.InnerPricing}>
                  <p className={totalPrice > 99 ? styles.fadeOutCollapse : styles.startText} >
                    STARTING
                    <span className={styles.Span01}> at just</span>
                  </p>
                  <div className={styles.price}>
                    <span className={styles.currency}>{currencySymbol}</span>
                    <span className={styles.amount}>{integerPart}</span>
                    <span className={styles.decimal}>.{decimalPart}</span>
                  </div>
                  {/* ANkush Code End */}
                </div>
                <p className={styles.subText}>
                  {calls} calls × {currencySymbol}
                  {unitPrice ? Number(unitPrice).toFixed(4) : "0.0000"} per call
                </p>
              </div>

              {/* Features Section */}
              {totalPrice < 109 && (
                <div className={styles.IconStandardFeature}>
                  <div className={styles.featureIcons}>
                    {/* svg omitted for brevity (kept same as your original) */}
                    <svg
                      width="60"
                      height="56"
                      viewBox="0 0 60 56"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.7908 6.79181V0.5H8.04661V6.79113C3.77866 7.45523 0.5 11.1435 0.5 15.5932C0.5 20.043 3.77866 23.7319 8.04661 24.3953V55.3844H10.7908V24.3953C15.0574 23.7319 18.3374 20.0437 18.3374 15.5932C18.3374 11.1428 15.0574 7.45523 10.7908 6.79181ZM9.42558 21.7677H9.41186C6.01108 21.7643 3.24422 18.9954 3.24422 15.5932C3.24422 12.189 6.01383 9.41872 9.41872 9.41872C12.8236 9.41872 15.5932 12.189 15.5932 15.5932C15.5932 18.9954 12.8264 21.7643 9.42558 21.7677ZM31.3725 31.4878V0.5H28.6283V31.4878C24.3603 32.1519 21.0817 35.8408 21.0817 40.2912C21.0817 44.741 24.3603 48.4292 28.6283 49.0933V55.3844H31.3725V49.0933C35.6391 48.4292 38.9191 44.741 38.9191 40.2912C38.9191 35.8408 35.6391 32.1519 31.3725 31.4878ZM30.0004 46.4657C26.5955 46.4657 23.8259 43.6961 23.8259 40.2912C23.8259 36.8856 26.5955 34.1153 30.0004 34.1153C33.4053 34.1153 36.1749 36.8856 36.1749 40.2912C36.1749 43.6961 33.4053 46.4657 30.0004 46.4657ZM59.5008 15.5932C59.5008 11.1428 56.2207 7.45455 51.9542 6.79113V0.5H49.2099V6.79113C44.942 7.45523 41.6633 11.1435 41.6633 15.5932C41.6633 20.043 44.942 23.7319 49.2099 24.3953V55.3844H51.9542V24.3953C56.2207 23.7319 59.5008 20.0437 59.5008 15.5932ZM50.5889 21.7677H50.5752C47.173 21.7643 44.4076 18.9954 44.4076 15.5932C44.4076 12.189 47.1772 9.41872 50.5821 9.41872C53.987 9.41872 56.7566 12.189 56.7566 15.5932C56.7566 18.9954 53.9897 21.7643 50.5889 21.7677Z"
                        fill="#6524EB"
                        stroke="#6524EB"
                      />
                    </svg>
                  </div>

                  <div className={styles.features}>
                    <span>Free VoIP #</span>
                    <span>Chat & Call Widget</span>
                    <span>Built-In CRM</span>
                    <span>Free 20 min * </span>
                    <span>+ Standard Feature</span>
                  </div>
                </div>
              )}
              {/* Slider Section */}
              <div className={styles.sliderSection}>
                <div
                  className={styles.callLabel}
                  style={{ left: `calc(${getTooltipPosition()}% - 0px)` }}
                >
                  {isYearly ? renderYearlyCalls() : formatCalls(calls) + "m"}
                </div>

                <div className={styles.sliderWrapper}>
                  <input
                    type="range"
                    min={min}
                    max={max}
                    step="10"
                    value={calls}
                    onChange={(e) => setCalls(Number(e.target.value))}
                    className={styles.slider}
                    style={{
                      background: `linear-gradient(to right, #6828F9 ${percentage}%, #ffffff ${percentage}%)`,
                      "--thumb-size": `${thumbSize}px`,
                    }}
                  />
                </div>

                <div className={styles.sliderText}>
                  <span>{min} min</span>
                  <span>{max.toString().replace("999", "") || "4K"} min</span>
                </div>
              </div>

              <p className={styles.bottomText}>
                Select the number of <b>Calls</b> your Business needs
              </p>
              {/* <div className={styles.LastBar}>
            <p>* For new users, valid for the first month only.</p>
          </div> */}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={styles.StartNowBtnSetup}>
          <div className={styles.BtnDiv} onClick={handleStartNow}>
            <StartNowBtn />
          </div>
          <p className={styles.bottomBelowText}>
            Get the most <a href="#">Intelligent & Pre-Trained</a> Receptionist,
            Superfast!
          </p>
        </div>
      </div>
    </>
  );
};

export default StartingCustomPlan;
